import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';

import { getColleagueEngineers, getColleagueTickets, getPudoLocations } from '../../api/colleague-ticket-queries.js';
import { getServiceDeskRecipients } from '../../api/service-desk-queries.js';
import { getMyPartnerCompany } from '../../api/user-queries.js';
import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';
import { ActionsMenu, showsDellLinks } from './_actions-menu.js';
import { ReassignmentNotice } from './_reassignment-notice.js';
import { formatReplyCode } from './_reply-code.js';
import { ServiceDeskButton, TicketCard } from './_ticket-card.js';
import { TicketsMapView } from './_tickets-map-view.js';

/**
 * "Other FE tickets & map" — a field engineer's view of their colleagues' open tickets.
 *
 * Read-only by design: the cards deliberately do not link through to the ticket detail page (an FE
 * may look at a colleague's job, not work it). The one action is the mail button, which opens the
 * FE's own mail client pre-addressed to the Dell team leader so they can ask to take the job over.
 */

/** `yyyy-mm-dd` for today, in the browser's timezone — what the date input expects. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const ColleagueTicketsPage = stateful(() => {
  const [date, setDate] = useState(todayIso());
  const [engineerSysId, setEngineerSysId] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'list' | 'map'>('list');

  // Debounce the ticket-number box the same way the self-ticket overview does.
  useEffect(() => {
    const id = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== search) setSearch(next);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const engineersQuery = useQuery(() => getColleagueEngineers()({ sysId: 1, name: 1, email: 1 }), []);

  const ticketsQuery = useQuery(
    () =>
      getColleagueTickets(
        date,
        engineerSysId || undefined,
        search || undefined,
      )({
        number: 1,
        title: 1,
        assignedToName: 1,
        state: 1,
        stateLabel: 1,
        locationLabel: 1,
        locationLat: 1,
        locationLng: 1,
        windowStart: 1,
        windowEnd: 1,
        fulfilmentStatusLabel: 1,
        correlationId: 1,
        clientName: 1,
        contactName: 1,
        contactPhone: 1,
        workNotes: { date: 1, html: 1 },
        replyCode: 1,
        replyCodeMeaning: 1,
        replyCodeDisplay: 1,
        partsStatusLabel: 1,
        parts: { modelNumber: 1, quantity: 1, stage: 1 },
      }),
    [date, engineerSysId, search],
  );

  // Depots are independent of the filters, so they load once and are reused by both views.
  const pudosQuery = useQuery(
    () =>
      getPudoLocations()({
        sysId: 1,
        name: 1,
        company: 1,
        street: 1,
        zipCode: 1,
        city: 1,
        siteAccessHours: 1,
        lat: 1,
        lng: 1,
      }),
    [],
  );

  // Drives the Dell group of the actions menu. Those are internal Dell/NSC tools, so they stay
  // hidden for anyone else — including while the query is still in flight, which keeps them from
  // flashing into view and back out on load.
  const myCompanyQuery = useQuery(() => getMyPartnerCompany()({ _id: 1, name: 1 }), []);
  // Who the "Email service desk" button addresses (set on the partner company; empty opens the mail with the address left blank).
  const recipientsQuery = useQuery(() => getServiceDeskRecipients()({ to: 1, cc: 1 }), []);

  const engineers = engineersQuery.value ?? [];
  const tickets = ticketsQuery.value ?? [];
  const pudos = pudosQuery.value ?? [];
  const recipients = recipientsQuery.value ?? null;
  const dellLinksVisible = showsDellLinks(myCompanyQuery.value?.name);

  // ── Filters ──────────────────────────────────────────────────────────────────────────────
  const filters = () => html`
    <div class="flex flex-wrap items-end gap-2">
      <label class="form-control">
        <span class="label-text text-xs">${m.colleague_filter_date()}</span>
        <input
          type="date"
          class="input input-bordered input-sm"
          .value=${date}
          @change=${(e: Event) => setDate((e.target as HTMLInputElement).value || todayIso())}
        />
      </label>

      <label class="form-control flex-1 min-w-40">
        <span class="label-text text-xs">${m.colleague_filter_engineer()}</span>
        <select
          class="select select-bordered select-sm w-full"
          .value=${engineerSysId}
          @change=${(e: Event) => setEngineerSysId((e.target as HTMLSelectElement).value)}
        >
          <option value="">${m.colleague_filter_all_engineers()}</option>
          ${engineers.map(fe => html`<option value=${fe.sysId ?? ''} ?selected=${fe.sysId === engineerSysId}>${fe.name}</option>`)}
        </select>
      </label>

      <label class="form-control flex-1 min-w-40">
        <span class="label-text text-xs">&nbsp;</span>
        <input
          type="search"
          class="input input-bordered input-sm w-full"
          placeholder=${m.colleague_search_placeholder()}
          .value=${searchInput}
          @input=${(e: Event) => setSearchInput((e.target as HTMLInputElement).value)}
        />
      </label>
    </div>
  `;

  // ── One ticket card ──────────────────────────────────────────────────────────────────────
  // The card itself is the shared TicketCard (also used by the FE's own Tickets list); only what is
  // specific to this view — the legacy-tool icon rail and the mail button — is passed in as slots.
  const ticketCard = (ticket: ServiceNowTicket) =>
    TicketCard(ticket, {
      // The parts state (puzzle icon) is drawn by the shared card. The reply-code flag only appears when a ticket
      // actually carries a `replycode`, which ServiceNow rarely sends yet.
      railExtra: html`
        ${(() => {
          // Badge shows the short display code (PND / CUS / CNL3); the tooltip carries
          // ServiceNow's full label. A code outside NSC's 16 pairs is shown in a warning tone
          // rather than dropped, so it's visibly unrecognised instead of silently wrong.
          // No code on the ticket (the overwhelming majority) renders nothing at all rather
          // than an empty flag — the rail is only worth the space when it says something.
          const reply = formatReplyCode(ticket);
          if (!reply) return nothing;
          return html`
            <span
              class="flex items-center gap-1 font-medium ${reply.known ? 'text-info' : 'text-warning'}"
              title=${reply.tooltip}
            >
              <span class="icon-[material-symbols--flag-outline] text-sm"></span>
              ${reply.label}
            </span>
          `;
        })()}
      `,
      footer: ServiceDeskButton(ticket, recipients),
    });

  // ── Regions ──────────────────────────────────────────────────────────────────────────────
  const listRegion = () => {
    if (ticketsQuery.loading && tickets.length === 0) {
      return html`<div class="flex items-center justify-center flex-1 py-10">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>`;
    }
    if (ticketsQuery.error) {
      return html`<div class="flex items-center justify-center flex-1 text-error py-10">${m.colleague_error()}</div>`;
    }
    if (tickets.length === 0) {
      return html`
        <div class="flex flex-col items-center justify-center flex-1 text-base-content/50 gap-2 py-10">
          <span class="icon-[material-symbols--group-outline] text-4xl"></span>
          <p class="text-center text-sm">${m.colleague_empty()}</p>
        </div>
      `;
    }
    return html`
      <div class="flex-1 min-h-0 flex flex-col gap-1">
        <div class="text-xs text-base-content/50">
          <!-- Separate singular key rather than an ICU plural: the message-format plugin in use
               here takes plain {placeholders} only, and the zero case never renders (the empty
               state replaces the whole list). -->
          ${
            tickets.length === 1
              ? m.colleague_list_count_one()
              : m.colleague_list_count({ count: String(tickets.length) })
          }
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">${tickets.map(ticketCard)}</div>
      </div>
    `;
  };

  return html`
    <div class="p-4 flex flex-col gap-3 h-full">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <h1 class="text-2xl font-bold">${m.colleague_title()}</h1>
          <p class="text-sm text-base-content/60">${m.colleague_subtitle()}</p>
        </div>
        ${ActionsMenu({ tickets, pudos, showDellLinks: dellLinksVisible, csvFilename: `colleague-addresses-${date}.csv` })}
      </div>

      ${ReassignmentNotice()}

      ${filters()}

      <!-- A joined button pair rather than daisyUI tabs: this switches a whole view, so it should
           read as a control, not as page chrome. aria-pressed carries the state for screen readers
           now that the tablist semantics are gone. -->
      <div class="join self-start">
        <button
          class="btn btn-sm join-item gap-1 ${view === 'list' ? 'btn-primary' : 'btn-outline'}"
          aria-pressed=${view === 'list'}
          @click=${() => setView('list')}
        >
          <span class="icon-[material-symbols--format-list-bulleted] text-base"></span>
          ${m.colleague_view_list()}
        </button>
        <button
          class="btn btn-sm join-item gap-1 ${view === 'map' ? 'btn-primary' : 'btn-outline'}"
          aria-pressed=${view === 'map'}
          @click=${() => setView('map')}
        >
          <span class="icon-[material-symbols--map-outline] text-base"></span>
          ${m.colleague_view_map()}
        </button>
      </div>

      ${view === 'list' ? listRegion() : TicketsMapView({ tickets, pudos, recipients })}
    </div>
  `;
});
